import { useCallback, useEffect } from 'react';
import {
  applyRowAdjustments,
  buildAdjustmentMap as buildTimelineAdjustmentMap,
  buildMemoMap as buildTimelineMemoMap,
  buildTimelineRows,
  normalizeTimetableTargetDate as normalizeTargetDate,
} from '../../utils/timetableTimeline';
/* eslint-disable react-hooks/exhaustive-deps */

export const buildAdjustmentMap = buildTimelineAdjustmentMap;
export const buildMemoMap = buildTimelineMemoMap;

export default function useEventDetailsRuntimePreview(context) {
  const {
    DEFAULT_TIMETABLE_COLUMNS,
    DEFAULT_TITLE_STYLE,
    activeTimetableTab,
    adjustmentsMap,
    clearHighlightTimer,
    getDefaultColumnAlign,
    highlightTimerRef,
    setNowTimetableRowId,
    timetables,
  } = context;
    const buildPreviewColumns = (data = {}) => {
      const cols = Array.isArray(data.columns) && data.columns.length ? data.columns : DEFAULT_TIMETABLE_COLUMNS;
      return cols.map((c) => ({
        ...c,
        align: c.align || getDefaultColumnAlign(c.id),
      }));
    };

    const buildPreviewRows = useCallback((data = {}) => {
      if (Array.isArray(data.rows) && data.rows.length) return data.rows;
      return [
        {
          id: 'row-1',
          startTime: '',
          duration: '',
          item: '',
          rowType: 'normal',
          rowColor: '',
          rowTextColor: '',
          cells: {},
        },
      ];
    }, []);

    const buildHeaderGroups = (columns = []) => {
      const groups = [];
      let idx = 0;
      while (idx < columns.length) {
        const base = columns[idx];
        const baseLabel = (base.label || '').trim();
        let span = 1;
        idx += 1;
        while (idx < columns.length && (columns[idx].label || '').trim() === baseLabel) {
          span += 1;
          idx += 1;
        }
        groups.push({
          label: baseLabel,
          span,
          align: base.align || getDefaultColumnAlign(base.id),
        });
      }
      return groups;
    };

    const pickColumnWidths = (data = {}, columns = []) => {
      const raw = data.columnWidths && typeof data.columnWidths === 'object' ? data.columnWidths : {};
      const next = {};
      columns.forEach((c) => {
        const w = raw[c.id];
        if (typeof w === 'number' && Number.isFinite(w) && w > 0) {
          next[c.id] = w;
        }
      });
      return next;
    };

    const pickRowHeights = (data = {}, rows = []) => {
      const raw = data.rowHeights && typeof data.rowHeights === 'object' ? data.rowHeights : {};
      const next = {};
      rows.forEach((r) => {
        const h = raw[r.id];
        if (typeof h === 'number' && Number.isFinite(h) && h > 0) {
          next[r.id] = h;
        }
      });
      return next;
    };

    const findNowRowId = useCallback((table, adjustmentsMap) => {
      const ymd = normalizeTargetDate(table?.target_date);
      if (!ymd) return null;
      const data = table.data || {};
      const rows = applyRowAdjustments(buildPreviewRows(data), adjustmentsMap);
      const timeline = buildTimelineRows(rows, ymd);
      const nowMs = Date.now();
      let candidate = null;

      timeline.forEach((r) => {
        if (r.startMs <= nowMs && nowMs < r.endMs) {
          if (!candidate || candidate.startMs < r.startMs) {
            candidate = { id: r.id, startMs: r.startMs };
          }
        }
      });
      return candidate?.id || null;
    }, [buildPreviewRows]);

    const findNextStartMs = useCallback((table, adjustmentsMap) => {
      const ymd = normalizeTargetDate(table?.target_date);
      if (!ymd) return null;
      const data = table.data || {};
      const rows = applyRowAdjustments(buildPreviewRows(data), adjustmentsMap);
      const timeline = buildTimelineRows(rows, ymd);
      const nowMs = Date.now();
      let next = null;
      timeline.forEach((r) => {
        if (r.startMs > nowMs && (next === null || r.startMs < next)) {
          next = r.startMs;
        }
      });
      return next;
    }, [buildPreviewRows]);

    const scheduleHighlightTick = useCallback(() => {
      clearHighlightTimer();
      const active = timetables.find((t) => t.id === activeTimetableTab) || timetables[0];
      if (!active) {
        setNowTimetableRowId(null);
        return;
      }
      setNowTimetableRowId(findNowRowId(active, adjustmentsMap));

      const nextStart = findNextStartMs(active, adjustmentsMap);
      if (!nextStart) return;

      const now = Date.now();
      const diff = nextStart - now;
      let delay = 10000;
      if (diff <= 0) delay = 500;
      else if (diff < 1000) delay = Math.max(120, diff);
      else if (diff < 5000) delay = 500;
      else if (diff < 15000) delay = 1000;
      else if (diff < 60000) delay = 3000;
      highlightTimerRef.current = setTimeout(scheduleHighlightTick, delay);
    }, [activeTimetableTab, adjustmentsMap, clearHighlightTimer, findNextStartMs, findNowRowId, timetables]);

    useEffect(() => {
      scheduleHighlightTick();
      return clearHighlightTimer;
    }, [scheduleHighlightTick, clearHighlightTimer]);

    const pickTitleStyle = (data = {}) => ({
      ...DEFAULT_TITLE_STYLE,
      ...(data.titleStyle || {}),
    });
  return {
    buildPreviewRows,
    buildHeaderGroups,
    pickColumnWidths,
    pickRowHeights,
    pickTitleStyle,
    applyRowAdjustments,
    buildPreviewColumns,
  };
}
